import { gql } from '@apollo/client';
import { SURVEY_QUESTION_FIELDS } from '@/graphql/fragments/surveys';
import { PRICING_TARIFF_FIELDS } from '@/graphql/fragments/pricing_tariffs';
import { RATE_FIELDS } from '@/graphql/fragments/rates';

export { SEARCH_SHIPPERS } from '../queries';

/**
 * Webhook subscriptions and integration overrides between this carrier and its shippers, for the
 * setup status on each shipper row. Feed the result to `getSetupStatusByShipper`. An override counts
 * for the carrier as `carrier_id`, or as `middle_mile_carrier_id` when it has no `carrier_id`, as on
 * the corporate Integrations card.
 */
export const GET_SHIPPER_INTEGRATION_STATUS = gql`
    query ShipperIntegrationStatus($carrier_id: uuid!, $shipper_ids: [uuid!]!) {
        hooks: integration_event_hooks(where: { carrier_id: { _eq: $carrier_id }, shipper_id: { _in: $shipper_ids } }) {
            event_subscription_id
            shipper_id
        }
        overrides: integration_overrides(
            where: {
                shipper_id: { _in: $shipper_ids }
                _or: [
                    { carrier_id: { _eq: $carrier_id } }
                    { carrier_id: { _is_null: true }, middle_mile_carrier_id: { _eq: $carrier_id } }
                ]
            }
        ) {
            override_id
            shipper_id
            type
            rec_isa_id
            send_isa_id
            status
        }
    }
`;

/** The shipper's enabled survey questions of the given types, for the Surveys page. */
export const GET_SHIPPER_SURVEY_QUESTIONS = gql`
    ${SURVEY_QUESTION_FIELDS}
    query ShipperSurveyQuestions($client_id: uuid!, $survey_types: [String!]!) {
        survey_questions(
            where: { client_id: { _eq: $client_id }, survey_type: { _in: $survey_types }, enabled: { _eq: true } }
        ) {
            ...SurveyQuestionFields
        }
    }
`;

/**
 * The carrier's side of its pricing with a shipper: `pricing_overrides` owned by the carrier with
 * partner = shipper, OMS or not (the corporate screen's "Carrier Pricing"). Disabled ones are left
 * out; an unset `enabled` counts as on.
 */
export const GET_CARRIER_ORDER_TARIFFS_FOR_SHIPPER = gql`
    ${PRICING_TARIFF_FIELDS}
    ${RATE_FIELDS}
    query CarrierOrderTariffsForShipper($carrier_id: uuid!, $shipper_id: uuid!) {
        orderOverrides: pricing_overrides(
            where: {
                client_id: { _eq: $carrier_id }
                partner_client_id: { _eq: $shipper_id }
                _or: [{ enabled: { _eq: true } }, { enabled: { _is_null: true } }]
                tariff_id: { _is_null: false }
            }
            order_by: { override_type: asc_nulls_last }
        ) {
            pricing_override_id
            override_type
            add_fuel_surcharge
            tariff {
                ...PricingTariffFields
                rates {
                    ...RateFields
                }
            }
        }
    }
`;

/**
 * The carrier's driver tariff mappings. They have no shipper link, so this is its own query on the
 * carrier alone and Apollo caches it once for every shipper's page.
 */
export const GET_CARRIER_DRIVER_TARIFFS = gql`
    ${PRICING_TARIFF_FIELDS}
    ${RATE_FIELDS}
    query CarrierDriverTariffs($carrier_id: uuid!) {
        driverMappings: driver_tariff_mappings(
            where: { client_id: { _eq: $carrier_id }, tariff_id: { _is_null: false } }
        ) {
            mapping_id
            driver_id
            tariff {
                ...PricingTariffFields
                rates {
                    ...RateFields
                }
            }
        }
    }
`;

/** Names of the subregions (areas) that area-based tariff rates point at, for the Billing / Tariffs rate table. */
export const GET_SUBREGION_NAMES = gql`
    query SubregionNames($subregion_ids: [uuid!]!) {
        subregions(where: { subregion_id: { _in: $subregion_ids } }) {
            subregion_id
            name
        }
    }
`;
